import React, { useState, useEffect, useRef } from 'react';

export default function ChatPanel({ 
  isChatExpanded, 
  setIsChatExpanded, 
  chatMessages, 
  chatInput, 
  setChatInput, 
  sendChatMessage, 
  formatTimestamp,
  currentUserId,
  containerStyle = {}
}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const lastMessageCountRef = useRef(chatMessages.length);
  const [messageTimestamps, setMessageTimestamps] = useState([]); // Track message send times
  const [rateLimitCooldown, setRateLimitCooldown] = useState(0); // Cooldown in seconds

  // Rate limit configuration
  const MAX_MESSAGES = 10;
  const RATE_LIMIT_WINDOW = 60000; // 60 seconds in milliseconds

  // Track new messages when chat is collapsed
  useEffect(() => {
    if (chatMessages.length > lastMessageCountRef.current && !isChatExpanded) {
      setUnreadCount(prev => prev + (chatMessages.length - lastMessageCountRef.current));
    }
    lastMessageCountRef.current = chatMessages.length;
  }, [chatMessages, isChatExpanded]);

  // Reset unread count when chat is expanded
  useEffect(() => {
    if (isChatExpanded) {
      setUnreadCount(0);
    }
  }, [isChatExpanded]);

  // Cooldown timer
  useEffect(() => {
    if (rateLimitCooldown > 0) {
      const timer = setTimeout(() => {
        setRateLimitCooldown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [rateLimitCooldown]);

  const checkRateLimit = () => {
    const now = Date.now();
    // Remove timestamps older than the rate limit window
    const recentTimestamps = messageTimestamps.filter(ts => now - ts < RATE_LIMIT_WINDOW);
    
    if (recentTimestamps.length >= MAX_MESSAGES) {
      // Calculate cooldown time
      const oldestRelevantTimestamp = recentTimestamps[0];
      const cooldownMs = RATE_LIMIT_WINDOW - (now - oldestRelevantTimestamp);
      setRateLimitCooldown(Math.ceil(cooldownMs / 1000));
      return false;
    }
    
    return true;
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!chatInput.trim()) return;
    
    if (!checkRateLimit()) {
      return; // Rate limit exceeded
    }
    
    // Add current timestamp
    const now = Date.now();
    setMessageTimestamps(prev => [...prev.filter(ts => now - ts < RATE_LIMIT_WINDOW), now]);
    
    sendChatMessage(e);
  };

  const isRateLimited = rateLimitCooldown > 0;

  return (
    <div style={{
      position: 'absolute',
      bottom: '10px',
      left: '10px',
      background: 'rgba(0,0,0,0.9)',
      color: 'white',
      borderRadius: '8px',
      fontFamily: 'Arial, sans-serif',
      width: isChatExpanded ? '320px' : '155px',
      height: isChatExpanded ? '300px' : '40px',
      border: '2px solid #4CAF50',
      transition: 'all 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      ...containerStyle
    }}>
      {/* Chat Header */}
      <div 
        onClick={() => setIsChatExpanded(!isChatExpanded)}
        style={{
          padding: '10px 15px',
          backgroundColor: 'rgba(76, 175, 80, 0.8)',
          cursor: 'pointer',
          borderRadius: '6px 6px 0 0',
          fontSize: '0.9em',
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: isChatExpanded ? '1px solid rgba(255,255,255,0.2)' : 'none'
        }}
      >
        <span>💬 Chat{unreadCount > 0 && ` (${unreadCount})`}</span>
        <span style={{ fontSize: '0.8em' }}>{isChatExpanded ? '▼' : '▲'}</span>
      </div>

      {/* Chat Content - Only visible when expanded */}
      {isChatExpanded && (
        <>
          {/* Messages Area */}
          <div style={{
            flex: 1,
            padding: '10px',
            overflowY: 'auto',
            fontSize: '0.8em',
            maxHeight: '200px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Powered by branding */}
            <div style={{
              textAlign: 'center',
              paddingBottom: '8px'
            }}>
              <span style={{ 
                fontSize: '0.7em', 
                color: 'rgba(255,255,255,0.5)',
                fontStyle: 'italic'
              }}>
                powered by{' '}
                <a 
                  href="https://luckoff.app/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    color: '#4CAF50',
                    textDecoration: 'none',
                    fontWeight: 'bold'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  Luck Off Chat
                </a>
              </span>
            </div>

            {chatMessages.length === 0 ? (
              <div style={{ 
                color: 'rgba(255, 255, 255, 0.6)', 
                fontStyle: 'italic',
                textAlign: 'center',
                marginTop: '20px'
              }}>
                No messages yet. Say hello! 👋
              </div>
            ) : (
              chatMessages.slice(-20).map((msg) => {
                const isSystemMessage = msg.userId === 'SYSTEM';
                
                return (
                  <div key={msg.id} style={{
                    marginBottom: '8px',
                    padding: isSystemMessage ? '10px' : '4px 8px',
                    backgroundColor: isSystemMessage 
                      ? 'rgba(76,175,80,0.3)' 
                      : (msg.userId === currentUserId ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.05)'),
                    borderRadius: '4px',
                    borderLeft: `3px solid ${msg.playerColor}`,
                    wordWrap: 'break-word',
                    border: isSystemMessage ? '1px solid rgba(76,175,80,0.5)' : 'none'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '2px'
                    }}>
                      <span style={{ 
                        fontWeight: 'bold', 
                        color: msg.playerColor,
                        fontSize: '0.85em'
                      }}>
                        {msg.displayName}
                        {msg.userId === currentUserId && !isSystemMessage && ' (You)'}
                      </span>
                      <span style={{ 
                        fontSize: '0.7em', 
                        color: 'rgba(255,255,255,0.5)' 
                      }}>
                        {formatTimestamp(msg.timestamp)}
                      </span>
                    </div>
                    <div style={{ 
                      color: 'rgba(255,255,255,0.9)',
                      lineHeight: '1.3',
                      whiteSpace: 'pre-line',
                      fontSize: isSystemMessage ? '0.75em' : 'inherit'
                    }}>
                      {msg.message}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Message Input */}
          <form onSubmit={handleSendMessage} style={{
            padding: '10px',
            borderTop: '1px solid rgba(255,255,255,0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
          }}>
            {isRateLimited && (
              <div style={{
                fontSize: '0.7em',
                color: '#FF6B6B',
                textAlign: 'center',
                padding: '2px'
              }}>
                ⏱️ Slow down! Wait {rateLimitCooldown}s
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message..."
                maxLength={500}
                disabled={isRateLimited}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  backgroundColor: isRateLimited ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
                  color: isRateLimited ? 'rgba(255,255,255,0.4)' : 'white',
                  fontSize: '0.8em',
                  outline: 'none',
                  cursor: isRateLimited ? 'not-allowed' : 'text'
                }}
                onFocus={(e) => {
                  if (!isRateLimited) {
                    e.target.style.borderColor = '#4CAF50';
                    e.target.style.backgroundColor = 'rgba(255,255,255,0.15)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                  e.target.style.backgroundColor = isRateLimited ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)';
                }}
              />
              <button
                type="submit"
                disabled={!chatInput.trim() || isRateLimited}
                style={{
                  padding: '6px 12px',
                  backgroundColor: (chatInput.trim() && !isRateLimited) ? '#4CAF50' : 'rgba(255,255,255,0.3)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (chatInput.trim() && !isRateLimited) ? 'pointer' : 'not-allowed',
                  fontSize: '0.8em',
                  fontWeight: 'bold',
                  transition: 'background-color 0.2s'
                }}
              >
                Send
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}